/**
 * A equipe comercial: quem assina proposta.
 *
 * Cadastro pequeno de propósito — o formulário é um diálogo, não uma página. São quatro campos, e
 * abrir uma tela inteira para eles faria a pessoa perder o lugar na lista.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import consultantService from '../../services/consultantService';
import type { Consultor, DadosDoConsultor } from '../../types/consultant';

const VAZIO: DadosDoConsultor = {
  name: '',
  role: 'Consultor comercial',
  phone: null,
  email: null,
  isActive: true,
};

/**
 * O que a pessoa digitou, com vazio virando nulo.
 *
 * **Não apara espaços.** Aparar aqui parece inofensivo e quebra o campo inteiro: o `onChange`
 * roda a cada tecla, então digitar "Paulo" e apertar espaço devolveria "Paulo" ao input
 * controlado — e a pessoa nunca conseguiria escrever a segunda palavra. Quem apara é o servidor,
 * na hora de gravar, que é quando aparar significa alguma coisa.
 */
function comoDigitado(valor: string): string | null {
  return valor === '' ? null : valor;
}

const colunas: DataTableColumn<Consultor>[] = [
  { id: 'name', label: 'Nome', primary: true, render: (c) => c.name },
  { id: 'role', label: 'Assina como', render: (c) => c.role ?? '—' },
  { id: 'phone', label: 'Telefone', render: (c) => c.phone ?? '—' },
  { id: 'email', label: 'E-mail', render: (c) => c.email ?? '—' },
  {
    id: 'isActive',
    label: 'Situação',
    render: (c) =>
      c.isActive ? (
        <Chip size="small" color="success" variant="outlined" label="Ativo" />
      ) : (
        <Chip size="small" label="Inativo" />
      ),
  },
];

export const ConsultantListPage: React.FC = () => {
  const [consultores, setConsultores] = useState<Consultor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [emEdicao, setEmEdicao] = useState<Consultor | null>(null);
  const [aberto, setAberto] = useState(false);
  const [dados, setDados] = useState<DadosDoConsultor>(VAZIO);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setConsultores(await consultantService.listar());
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar os consultores');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const abrirNovo = useCallback(() => {
    setEmEdicao(null);
    setDados(VAZIO);
    setAberto(true);
  }, []);

  const abrirEdicao = useCallback((c: Consultor) => {
    setEmEdicao(c);
    setDados({
      name: c.name,
      role: c.role,
      phone: c.phone,
      email: c.email,
      isActive: c.isActive,
    });
    setAberto(true);
  }, []);

  const salvar = useCallback(async () => {
    if (dados.name.trim() === '') {
      setErro('O nome do consultor é obrigatório.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      if (emEdicao) await consultantService.atualizar(emEdicao.id, dados);
      else await consultantService.criar(dados);
      setAberto(false);
      await carregar();
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível salvar o consultor');
    } finally {
      setSalvando(false);
    }
  }, [carregar, dados, emEdicao]);

  const remover = useCallback(
    async (c: Consultor) => {
      try {
        await consultantService.remover(c.id);
        await carregar();
      } catch (err: unknown) {
        setErro(err instanceof Error ? err.message : 'Não foi possível excluir o consultor');
      }
    },
    [carregar]
  );

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Consultores' }]}
      title="Consultores Comerciais"
      subtitle="Quem assina a proposta — o nome, o telefone e o e-mail que saem no documento"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>
          Novo consultor
        </Button>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <DataTable
        columns={colunas}
        rows={consultores}
        getRowId={(c) => c.id}
        loading={carregando}
        onRowClick={abrirEdicao}
        emptyState={{
          title: 'Nenhum consultor cadastrado neste contexto',
          description:
            'Cadastre quem assina as propostas. Na hora de montar uma, é só escolher o nome — o telefone e o e-mail vão junto, sem digitar de novo.',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            tooltip: 'Editar',
            onClick: abrirEdicao,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            tooltip: 'Excluir',
            color: 'error',
            onClick: (c: Consultor) => void remover(c),
          },
        ]}
      />

      <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>{emEdicao ? 'Editar consultor' : 'Novo consultor'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label="Nome"
              value={dados.name}
              onChange={(e) => setDados({ ...dados, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Assina como"
              helperText="O que sai embaixo do nome no documento"
              value={dados.role ?? ''}
              onChange={(e) => setDados({ ...dados, role: comoDigitado(e.target.value) })}
            />
            <TextField
              fullWidth
              label="Telefone"
              value={dados.phone ?? ''}
              onChange={(e) => setDados({ ...dados, phone: comoDigitado(e.target.value) })}
            />
            <TextField
              fullWidth
              label="E-mail"
              value={dados.email ?? ''}
              onChange={(e) => setDados({ ...dados, email: comoDigitado(e.target.value) })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={dados.isActive}
                  onChange={(e) => setDados({ ...dados, isActive: e.target.checked })}
                />
              }
              label="Ativo — aparece na hora de montar a proposta"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void salvar()} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ConsultantListPage;
